import api from './api'

/**
 * Impressão de etiquetas — um caminho só, com o crachá renderizado pelo servidor.
 *
 * Havia quatro caminhos de impressão neste front, cada um montando o próprio HTML:
 * impressão individual, lote por seleção, lote por agrupamento e pós-cadastro. Todos em
 * 90x50mm com nome/empresa/cargo — campos de outro evento. O crachá configurado para o
 * SBAM é 82x30mm com nome, conselho profissional e estado, e é o que o briefing pede.
 *
 * Resultado prático: corrigir o modelo do crachá no admin não mudava nada do que saía na
 * impressora, porque nada do que saía vinha do modelo.
 *
 * Aqui existe um caminho só. Mudar o crachá passa a valer para os quatro botões, e para
 * o totem, que já usava o servidor.
 */

/** Abre o HTML num iframe oculto e dispara a impressão. Resolve quando o diálogo abriu. */
function imprimirHtml(html: string, idFrame: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const anterior = document.getElementById(idFrame)
    if (anterior) anterior.remove()

    const frame = document.createElement('iframe')
    frame.id = idFrame
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
    document.body.appendChild(frame)

    const doc = frame.contentWindow?.document
    if (!doc) {
      reject(new Error('Não foi possível preparar a impressão.'))
      return
    }

    doc.open()
    doc.write(html)
    doc.close()

    // Sem a espera, a impressora recebe a página antes de o QR em SVG ter sido desenhado
    // e a etiqueta sai com um retângulo vazio no lugar do código.
    const disparar = () => {
      setTimeout(() => {
        try {
          frame.contentWindow?.focus()
          frame.contentWindow?.print()
          resolve()
        } catch (e) {
          reject(e as Error)
        }
      }, 400)
    }

    frame.contentWindow?.addEventListener('load', disparar)
    // Rede lenta ou evento perdido não pode deixar o operador esperando para sempre.
    setTimeout(disparar, 3000)
  })
}

/**
 * Imprime as etiquetas dos códigos informados, na ordem em que vierem.
 *
 * Uma etiqueta por página: a impressora térmica avança por página, e empilhar duas numa
 * folha desalinha todas as seguintes.
 */
export async function imprimirEtiquetas(codes: string[]): Promise<void> {
  const limpos = codes.map(c => String(c || '').trim()).filter(Boolean)
  if (!limpos.length) throw new Error('Nenhuma credencial para imprimir.')

  const resp = await api.post('/badge/lote', { codes: limpos }, { responseType: 'text' })
  const html = typeof resp.data === 'string' ? resp.data : String(resp.data)
  await imprimirHtml(html, 'frame-etiquetas')
}
